import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

export const DeleteModal = ({ isOpen, onClose, onConfirm, itemName, itemType = 'item', loading = false }) => {
    const [confirmText, setConfirmText] = useState('');

    // Reset confirmation text when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setConfirmText('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const isDeleteEnabled = confirmText.toLowerCase() === 'delete';

    const handleConfirm = () => {
        if (isDeleteEnabled) {
            onConfirm();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={onClose}
                />

                <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Delete {itemType}
                        </h3>
                    </div>

                    <p className="text-gray-600 mb-4">
                        Are you sure you want to delete <strong>{itemName}</strong>?
                        This action cannot be undone.
                    </p>

                    <div className="mb-6">
                        <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-700 mb-2">
                            Type "delete" to confirm deletion:
                        </label>
                        <input
                            id="delete-confirm"
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="Type delete here"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            autoComplete="off"
                        />
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading || !isDeleteEnabled}
                            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                                isDeleteEnabled
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            } disabled:opacity-50`}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
